/**
 * MS-533 — payment methods and the Moyasar form, lifted out of the retired GuestPortalCheckout.
 *
 * `disabled` is the UI half of the rental-agreement gate. It is NOT the security boundary:
 * SaveReservationTransactionLog refuses to issue a payment description for an unsigned
 * reservation regardless of what this component renders.
 */
import { useEffect, useRef, useState } from "react";
import ApplePay from "@assets/svg/ApplePay.svg?react";
import CreditCard from "@assets/svg/CreditCard.svg?react";
import PaymentMethodItem from "./PaymentMethodItem";
import {
  PaymentMethodsSection,
  PaymentMethodsTitle,
  PaymentMethodsList,
  PaymentFormSection,
  PaymentFormContainer,
  PaymentFormTitle,
  CreditCardIconContainer,
  ApplePayIconContainer,
  PaymentDisabledOverlay,
} from "./styles";

const MOYASAR_SCRIPT_ID = "moyasar-script";
const MOYASAR_SRC =
  "https://cdn.jsdelivr.net/npm/moyasar-payment-form@2.2.2/dist/moyasar.umd.min.js";

// Return the guest to the same origin they are already on (dev / staging / prod), so the
// Moyasar callback never crosses environments.
const base = `${window.location.origin}/`;

declare global {
  interface Window {
    Moyasar: any;
  }
}

interface PaymentSectionProps {
  reservationId: string;
  propertyName: string;
  totalPrice: number;
  publicKey: string;
  paymentDescription: string;
  disabled: boolean;
  onCompleted: (payment: any) => Promise<void>;
}

const PaymentSection = ({
  reservationId,
  propertyName,
  totalPrice,
  publicKey,
  paymentDescription,
  disabled,
  onCompleted,
}: PaymentSectionProps) => {
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(1);
  const [scriptReady, setScriptReady] = useState(!!window.Moyasar);
  const formContainer = useRef<HTMLDivElement>(null);

  // Load once. The previous implementation created a <script> on EVERY render and appended it in
  // an effect that never cleaned up, stacking a new copy each time the component re-rendered.
  useEffect(() => {
    if (window.Moyasar) {
      setScriptReady(true);
      return;
    }

    const existing = document.getElementById(MOYASAR_SCRIPT_ID) as HTMLScriptElement | null;
    const script = existing ?? document.createElement("script");
    const onLoad = () => setScriptReady(true);

    if (!existing) {
      script.src = MOYASAR_SRC;
      script.async = true;
      script.defer = true;
      script.id = MOYASAR_SCRIPT_ID;
      document.body.appendChild(script);
    }
    script.addEventListener("load", onLoad);

    return () => script.removeEventListener("load", onLoad);
  }, []);

  const methods = selectedPaymentMethod === 1 ? ["creditcard"] : ["applepay"];

  useEffect(() => {
    if (disabled || !scriptReady || !formContainer.current || !window.Moyasar) return;

    window.Moyasar.init({
      element: formContainer.current,
      amount: totalPrice * 100,
      currency: "SAR",
      description: paymentDescription,
      publishable_api_key: publicKey,
      callback_url: `${base}guest/payment-status/${reservationId}`,
      // Same networks the direct booking website's form accepts.
      supported_networks: ["visa", "mastercard", "mada"],
      // Moyasar falls back to <html lang>, which is "en" in this app, so the form rendered in
      // English on an Arabic page. The website gets Arabic from its locale-driven <html lang>.
      language: "ar",
      methods,
      apple_pay: {
        country: "SA",
        label: propertyName,
        validate_merchant_url: "https://api.moyasar.com/v1/applepay/initiate",
      },
      on_completed: onCompleted,
    });
  }, [
    disabled,
    scriptReady,
    selectedPaymentMethod,
    totalPrice,
    paymentDescription,
    publicKey,
    propertyName,
    reservationId,
  ]);

  const paymentMethods = [
    {
      id: 1,
      title: "البطاقات الائتمانية",
      description: "الدفع بواسطة مدى، فيزا، ماستركارد.",
      icon: (
        <CreditCardIconContainer>
          <CreditCard width={24} height={24} />
        </CreditCardIconContainer>
      ),
      disabled,
    },
    {
      id: 2,
      title: "أبل باي",
      description: "دفع سريع وسلس",
      icon: (
        <ApplePayIconContainer>
          <ApplePay width={24} height={24} />
        </ApplePayIconContainer>
      ),
      disabled,
    },
  ];

  return (
    <PaymentDisabledOverlay $disabled={disabled}>
      <PaymentMethodsSection>
        <PaymentMethodsTitle>طريقة الدفع</PaymentMethodsTitle>
        <PaymentMethodsList>
          {paymentMethods.map((paymentMethod) => (
            <PaymentMethodItem
              key={paymentMethod.id}
              paymentMethod={paymentMethod}
              selectedPaymentMethod={selectedPaymentMethod}
              onSelect={disabled ? () => {} : setSelectedPaymentMethod}
            />
          ))}
        </PaymentMethodsList>
      </PaymentMethodsSection>

      {!disabled && (
        <PaymentFormSection>
          <PaymentFormContainer>
            <PaymentFormTitle>تفاصيل الدفع</PaymentFormTitle>
            <div className="mysr-form" ref={formContainer} />
          </PaymentFormContainer>
        </PaymentFormSection>
      )}
    </PaymentDisabledOverlay>
  );
};

export default PaymentSection;
